import TextField from "@/components/TextField";
import Icon from "@/components/Icon";
import PeopleSelectionLists from "./components/PeopleSelectionLists";
import { type EventHit, renderEventHit, InlineSearchResults } from "@/components/AlgoliaSearch";
import { InteractionInput } from "@/components/interactions/types";
import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { UnavailableEventBanner } from "./components/UnavailableEventBanner";
import { Event, Person } from "./CreateGlobalInteractionSideSheet";
import { EVENTS_QUERY } from "./queries";

type EventType = {
  gatheredEvents: Event[];
  ineligibleEvent: Event | undefined;
  loading?: boolean;
  peopleArray: Person[];
  setIneligibleEvent: (value: Event | undefined) => void;
  setCurrentEvent: (value: Event | undefined) => void;
  setSearchedForEvent: (value: boolean) => void;
  setVariables: (value: InteractionInput) => void;
  variables: InteractionInput;
};

const mapEventToHit = (event: Event): EventHit => ({
  objectID: event.id,
  id: event.id,
  title: event.title,
  slug: event.slug,
  startsAt: event.startsAt,
  endsAt: event.endsAt,
  city: event.city,
  state: event.state,
  facilityName: event.facility?.name
});

export const MultiPersonAdderEvent = ({
  gatheredEvents,
  ineligibleEvent,
  loading: parentLoading,
  peopleArray,
  setIneligibleEvent,
  setCurrentEvent,
  setSearchedForEvent,
  setVariables,
  variables
}: EventType) => {
  const [query, setQuery] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [eventSearch, setEventSearch] = useState<string>("");
  const [selectedEventTitle, setSelectedEventTitle] = useState<string | null>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  // Event grouping stays on `events(query: { title })`, not fuzzySearchActivities.
  // fuzzySearchActivities requires manageEvents; Create Interaction staff may lack that bit.
  // Hits without event.slug also cannot load eventRegistrants.
  const { data, loading } = useQuery(EVENTS_QUERY, {
    variables: { query: { title: eventSearch } },
    skip: eventSearch === ""
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setEventSearch(debouncedSearchTerm);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearchTerm]);

  const eventsArray: Event[] = data?.events?.edges?.map(({ node }: { node: Event }) => node) || [];
  const hasActiveQuery = query.trim() !== "" && selectedEventTitle === null;

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      const textField =
        searchBoxRef.current?.querySelector<HTMLElement>("md-outlined-text-field") ||
        searchBoxRef.current?.querySelector<HTMLElement>("md-filled-text-field");
      textField?.focus();
    });
  }, []);

  useEffect(() => {
    focusInput();
  }, [focusInput]);

  const handleClear = () => {
    setQuery("");
    setDebouncedSearchTerm("");
    setEventSearch("");
    setSelectedEventTitle(null);
    setCurrentEvent(undefined);
    setSearchedForEvent(false);
    setIneligibleEvent(undefined);
    focusInput();
  };

  const handleSelectEvent = (event: Event) => {
    setIneligibleEvent(undefined);
    setCurrentEvent({ ...event, index: gatheredEvents.length });
    setSearchedForEvent(true);
    setQuery(event.title);
    setSelectedEventTitle(event.title);
    setDebouncedSearchTerm("");
    setEventSearch("");
  };

  const trailingIcon = query ? (
    <button className="cursor-pointer flex items-center h-full" onClick={handleClear} aria-label="Clear search query">
      <Icon name="cancel" />
    </button>
  ) : undefined;

  const selectedPeople = peopleArray.filter((person) => person.selected);
  const availablePeople = peopleArray.filter((person) => !person.selected);

  return (
    <div className="space-y-6">
      <div ref={searchBoxRef}>
        <TextField
          label="Event"
          placeholder="Search for an event"
          value={query}
          onChangeText={(value) => {
            setQuery(value);
            setSelectedEventTitle(null);
            setDebouncedSearchTerm(value);
            setIneligibleEvent(undefined);
            if (value === "") {
              setEventSearch("");
              setSearchedForEvent(false);
              setCurrentEvent(undefined);
            }
          }}
          leadingIcon="search"
          trailingIcon={trailingIcon}
          variant="outlined"
          className="w-full"
          supportingText="Search for an event and then select registrants to add to the interaction"
          data-testid="event-search-input"
        />
      </div>

      {hasActiveQuery && (
        <InlineSearchResults
          results={eventsArray}
          isLoading={loading}
          getKey={(event) => event.id}
          renderItem={(event) => renderEventHit(mapEventToHit(event), () => handleSelectEvent(event))}
          noResultsMessage="No events found"
        />
      )}

      {ineligibleEvent && <UnavailableEventBanner event={ineligibleEvent} />}

      {!hasActiveQuery && (
        <PeopleSelectionLists
          selectedPeople={selectedPeople}
          availablePeople={availablePeople}
          loading={parentLoading}
          onSelect={(person) => {
            person.selected = true;
            setVariables({ ...variables, participantIds: [...(variables.participantIds || []), person.id] });
          }}
          onDeselect={(person) => {
            person.selected = false;
            setVariables({
              ...variables,
              participantIds: (variables.participantIds || []).filter((id: string) => id !== person.id)
            });
          }}
          onSelectAll={() => {
            availablePeople.forEach((p) => {
              p.selected = true;
            });
            setVariables({
              ...variables,
              participantIds: [...(variables.participantIds || []), ...availablePeople.map((p) => p.id)]
            });
          }}
          onDeselectAll={() => {
            selectedPeople.forEach((p) => {
              p.selected = false;
            });
            const idsToRemove = new Set(selectedPeople.map((p) => p.id));
            setVariables({
              ...variables,
              participantIds: (variables.participantIds || []).filter((id: string) => !idsToRemove.has(id))
            });
          }}
        />
      )}
    </div>
  );
};
